import {useEffect} from 'react';
import {observer} from 'mobx-react';
import {useLocation} from 'react-router-dom';
import AppStore from '../../app.store';
import NetworkStore from '../../network.store';
import {getFirebase} from '../../firebase.loader';
import TransactionsOfflineSyncWorker from '../../pages/transactions/transactions.offline.sync.worker';
import {redirectToLandingLogin} from '../../utils/greeting';

/** One-time app bootstrap: network monitor, init, global click handler. */
export function AppMountEffects() {
    useEffect(() => {
        const handleDocumentClick = () => {
            document
                .querySelector('.context-menu-focused')
                ?.classList.remove('context-menu-focused');
        };

        document.addEventListener('click', handleDocumentClick);
        NetworkStore.init();

        const params = new URLSearchParams(window.location.search);
        const isLandingAuthExchange =
            window.location.pathname === '/auth' && params.has('c');

        if (!isLandingAuthExchange) {
            void AppStore.init();
        }

        return () => {
            document.removeEventListener('click', handleDocumentClick);
            NetworkStore.dispose();
            TransactionsOfflineSyncWorker.dispose();
        };
    }, []);

    return null;
}

/** Reacts only to login state for Firebase and offline sync. */
export const AppSessionEffects = observer(() => {
    const {isLoggedIn} = AppStore;

    useEffect(() => {
        if (!isLoggedIn) {
            TransactionsOfflineSyncWorker.dispose();
            return undefined;
        }

        TransactionsOfflineSyncWorker.init();
        return () => TransactionsOfflineSyncWorker.dispose();
    }, [isLoggedIn]);

    useEffect(() => {
        if (!isLoggedIn) {
            void getFirebase().then((firebase) => {
                firebase.stopForegroundMessageListener();
            });
            return undefined;
        }

        let cancelled = false;

        void getFirebase().then((firebase) => {
            if (!cancelled) {
                firebase.startForegroundMessageListener();
            }
        });

        return () => {
            cancelled = true;
            void getFirebase().then((firebase) => {
                firebase.stopForegroundMessageListener();
            });
        };
    }, [isLoggedIn]);

    return null;
});

/** Redirects unauthenticated users away from protected routes. */
export const AppAuthRedirect = observer(() => {
    const {pathname} = useLocation();
    const {isLoggedIn} = AppStore;

    useEffect(() => {
        if (!isLoggedIn && pathname !== '/auth') {
            redirectToLandingLogin();
        }
    }, [isLoggedIn, pathname]);

    return null;
});
